import { useEffect, useState } from 'react';
import { Alert, Box, Button, Card, CardContent, Grid, MenuItem, TextField, Typography } from '@mui/material';
import { Refresh } from '@mui/icons-material';
import TaxBookPage from './TaxBookPage';
import { taxBooksService } from '../../../services/api';

const money = (value) => `$${(parseFloat(value || 0) || 0).toFixed(2)}`;

const salesTaxpayerColumns = [
  { key: 'fecha_emision_documento', label: 'Fecha' },
  { key: 'tipo_documento', label: 'Tipo DTE' },
  { key: 'numero_resolucion', label: 'No. control (sin guiones)' },
  { key: 'nit_o_nrc', label: 'NIT/NRC' },
  { key: 'nombre_razon_social_o_denominacion', label: 'Cliente' },
  { key: 'ventas_gravadas_locales', label: 'Gravadas' },
  { key: 'debito_fiscal', label: 'Débito fiscal' },
  { key: 'total_ventas', label: 'Total' },
];

const consumerFinalColumns = [
  { key: 'fecha_emision_documento', label: 'Fecha' },
  { key: 'tipo_documento', label: 'Tipo DTE' },
  { key: 'numero_documento_del', label: 'Cod. generación (del)' },
  { key: 'numero_documento_al', label: 'Cod. generación (al)' },
  { key: 'ventas_gravadas_locales', label: 'Gravadas (IVA incl.)' },
  { key: 'total_ventas', label: 'Total ventas' },
];

const SalesAnnexesPage = () => {
  const now = new Date();
  const [filters, setFilters] = useState({
    month: String(now.getMonth() + 1),
    year: String(now.getFullYear()),
    branchId: '',
  });
  const [branches, setBranches] = useState([]);
  const [error, setError] = useState('');
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    const loadBranches = async () => {
      try {
        const branchRows = await taxBooksService.getCatalog('branches');
        setBranches(branchRows || []);
      } catch {
        setBranches([]);
      }
    };

    loadBranches();
  }, []);

  const handleReload = () => {
    setError('');
    setReloadToken((prev) => prev + 1);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h5" component="h1" gutterBottom>
            Libro / Anexos de Ventas
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Generación del Anexo 1 (Ventas a contribuyentes) y Anexo 2 (Ventas a consumidor final) según formato MH.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Refresh />} onClick={handleReload}>
          Generar para el período
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={2}>
              <TextField
                select
                fullWidth
                label="Mes"
                value={filters.month}
                onChange={(event) => setFilters((prev) => ({ ...prev, month: event.target.value }))}
              >
                {Array.from({ length: 12 }, (_, index) => (
                  <MenuItem key={index + 1} value={String(index + 1)}>
                    {String(index + 1).padStart(2, '0')}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="Año"
                value={filters.year}
                onChange={(event) => setFilters((prev) => ({ ...prev, year: event.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                select
                fullWidth
                label="Sucursal"
                value={filters.branchId}
                onChange={(event) => setFilters((prev) => ({ ...prev, branchId: event.target.value }))}
                SelectProps={{ displayEmpty: true }}
                InputLabelProps={{ shrink: true }}
              >
                <MenuItem value="">Todas</MenuItem>
                {branches.map((branch) => (
                  <MenuItem key={branch.id} value={branch.id}>
                    {branch.branch_name} ({branch.codigo_establecimiento})
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <TaxBookPage
        title="Anexo 1 - Ventas a Contribuyentes"
        subtitle="Vista previa y exportación en CSV (formato MH) para DTE 03/05/06."
        previewPath="books/sales-taxpayers/preview"
        exportPath="books/sales-taxpayers/export"
        columns={salesTaxpayerColumns}
        summaryCards={[
          { key: 'count', label: 'Documentos' },
          { key: 'totalVentas', label: 'Total ventas', format: money },
          { key: 'totalDebitoFiscal', label: 'Débito fiscal', format: money },
        ]}
        extraFilters={[
          {
            key: 'documentType',
            label: 'Tipo DTE',
            options: [
              { value: '', label: 'Todos' },
              { value: '03', label: '03 - CCF' },
              { value: '05', label: '05 - Nota Crédito' },
              { value: '06', label: '06 - Nota Débito' },
            ],
          },
        ]}
        sharedFilters={filters}
        hideFilterCard
        reloadToken={reloadToken}
      />

      <Box sx={{ mt: 4 }}>
        <TaxBookPage
          title="Anexo 2 - Ventas a Consumidor Final"
          subtitle="Vista previa y exportación en CSV (formato MH) agrupado por fecha y tipo de DTE."
          previewPath="books/sales-consumer-final/preview"
          exportPath="books/sales-consumer-final/export"
          columns={consumerFinalColumns}
          summaryCards={[
            { key: 'count', label: 'Filas' },
            { key: 'totalVentas', label: 'Total ventas', format: money },
          ]}
          sharedFilters={filters}
          hideFilterCard
          reloadToken={reloadToken}
        />
      </Box>
    </Box>
  );
};

export default SalesAnnexesPage;

